/**
 * Permission system — mirrors GWT's PermissionManager + ClientFoundation role checks.
 *
 * Two layers:
 *   1. Role-based (ACL): "Is the user an admin for any site?" → acl entries like "role:site-admin"
 *   2. Permission-based: "Can the user download studies for site:root?" → permissionsMap
 *
 * Usage:
 *   const { isRootAdmin, isAdmin, hasPermission } = usePermissions();
 *   if (isAdmin) showAdminTabs();
 *   if (hasPermission('DOWNLOAD_STUDIES')) showDownloadButton();
 */

import { useMemo } from 'react';
import { useAuth } from './useAuth';
import type { User } from '../../api/types';

/* ── Role checks (from ACL array) ── */

/** Root admin has unrestricted access to everything */
export function isRootAdmin(user: User | null): boolean {
  return !!user?.acl?.includes('role:root-admin');
}

/** Admin for at least one site (or root admin) */
export function isAdmin(user: User | null): boolean {
  if (!user?.acl) return false;
  return user.acl.some((a) => a.endsWith('-admin'));
}

/** Admin or manager for at least one site */
export function isAdminOrManager(user: User | null): boolean {
  if (!user?.acl) return false;
  return user.acl.some((a) => a.endsWith('-admin') || a.endsWith('-manage'));
}

/** Admin for a specific site */
export function isSiteAdmin(user: User | null, siteObjKey: string): boolean {
  if (!user?.acl) return false;
  if (user.acl.includes('role:root-admin')) return true;
  const siteId = siteObjKey.startsWith('site:') ? siteObjKey.substring(5) : siteObjKey;
  return user.acl.includes(`role:${siteId}-admin`);
}

/** Check if user has a specific role suffix for any site (e.g. 'rad', 'res', 'coord') */
export function hasRole(user: User | null, roleSuffix: string): boolean {
  if (!user?.acl) return false;
  if (user.acl.includes('role:root-admin')) return true;
  return user.acl.some((a) => a.endsWith(`-${roleSuffix}`));
}

/** Radiologist for at least one site */
export function isRadiologist(user: User | null): boolean {
  return hasRole(user, 'rad');
}

/** Resident for at least one site */
export function isResident(user: User | null): boolean {
  return hasRole(user, 'res');
}

/** Coordinator for at least one site */
export function isCoordinator(user: User | null): boolean {
  return hasRole(user, 'coord');
}

/* ── Permission checks (from permissionsMap) ── */

/**
 * Check if user has a specific permission for any site.
 * Root admin always returns true.
 */
export function hasPermission(user: User | null, permission: string): boolean {
  if (!user) return false;
  if (isRootAdmin(user)) return true;
  if (!user.permissionsMap) return false;
  return Object.values(user.permissionsMap).some((perms) => perms.includes(permission));
}

/**
 * Check if user has a specific permission for a specific site.
 * Root admin always returns true.
 */
export function hasSitePermission(user: User | null, siteObjKey: string, permission: string): boolean {
  if (!user) return false;
  if (isRootAdmin(user)) return true;
  const perms = user.permissionsMap?.[siteObjKey];
  return !!perms && perms.includes(permission);
}

/* ── React hook ── */

export interface PermissionHelpers {
  /** Root admin — unrestricted access */
  isRootAdmin: boolean;
  /** Admin for at least one site */
  isAdmin: boolean;
  /** Admin or manager for at least one site */
  isAdminOrManager: boolean;
  /** Radiologist for at least one site */
  isRadiologist: boolean;
  /** Resident for at least one site */
  isResident: boolean;
  /** Check a specific permission (any site) */
  hasPermission: (permission: string) => boolean;
  /** Check a specific permission for a specific site */
  hasSitePermission: (siteObjKey: string, permission: string) => boolean;
  /** Check admin for a specific site */
  isSiteAdmin: (siteObjKey: string) => boolean;
  /** User type string from backend */
  userType: string;
}

/**
 * Hook that provides memoized permission helpers for the current user.
 * Uses ACL + permissionsMap from the login/validateSession response.
 */
export function usePermissions(): PermissionHelpers {
  const { user } = useAuth();

  return useMemo<PermissionHelpers>(() => ({
    isRootAdmin: isRootAdmin(user),
    isAdmin: isAdmin(user),
    isAdminOrManager: isAdminOrManager(user),
    isRadiologist: isRadiologist(user),
    isResident: isResident(user),
    hasPermission: (perm: string) => hasPermission(user, perm),
    hasSitePermission: (site: string, perm: string) => hasSitePermission(user, site, perm),
    isSiteAdmin: (site: string) => isSiteAdmin(user, site),
    userType: user?.userType ?? '',
  }), [user]);
}

/* ── Permission constants matching GWT Permissions enum ── */

export const Permissions = {
  // Studies
  DOWNLOAD_STUDIES: 'DOWNLOAD_STUDIES',
  SEND_STUDIES_TO: 'SEND_STUDIES_TO',
  UPLOAD_STUDIES: 'UPLOAD_STUDIES',
  DOWNLOAD_FOR_CD: 'DOWNLOAD_FOR_CD',
  ADD_STUDY_SHARES: 'ADD_STUDY_SHARES',
  COMPLETE_STUDIES: 'COMPLETE_STUDIES',
  ASSIGN_STUDIES: 'ASSIGN_STUDIES',
  RELEASE_STUDIES: 'RELEASE_STUDIES',
  ASSIGN_STUDIES_TO_RESIDENT: 'ASSIGN_STUDIES_TO_RESIDENT',
  HOLD_UNHOLD_STUDIES: 'HOLD_UNHOLD_STUDIES',
  MARK_READ: 'MARK_READ',
  MARK_UNREAD: 'MARK_UNREAD',
  MARK_DICTATED: 'MARK_DICTATED',
  MARK_TRANSCRIBED: 'MARK_TRANSCRIBED',
  MARK_INPROGRESS: 'MARK_INPROGRESS',
  EDIT_STUDIES: 'EDIT_STUDIES',
  EDIT_NON_ASSIGNED_STUDIES: 'EDIT_NON_ASSIGNED_STUDIES',
  DELETE_PURE_STUDIES: 'DELETE_PURE_STUDIES',
  DELETE_ANY_STUDIES: 'DELETE_ANY_STUDIES',
  DELETE_IMAGES_PURE_STUDIES: 'DELETE_IMAGES_PURE_STUDIES',
  DELETE_IMAGES_ANY_STUDIES: 'DELETE_IMAGES_ANY_STUDIES',
  VIEW_STUDY_EVENTS: 'VIEW_STUDY_EVENTS',
  ASSIGN_TO_ME: 'ASSIGN_TO_ME',
  EDIT_MISSED_SLA_REASONS: 'EDIT_MISSED_SLA_REASONS',
  CALL_CONTACTS: 'CALL_CONTACTS',
  AUTO_ADD_ORDERS: 'AUTO_ADD_ORDERS',
  // Reports
  VIEW_DRAFT_REPORTS: 'VIEW_DRAFT_REPORTS',
  ADD_DRAFT_REPORTS: 'ADD_DRAFT_REPORTS',
  SIGN_REPORTS: 'SIGN_REPORTS',
  ADD_ADDENDUMS: 'ADD_ADDENDUMS',
  SIGN_ADDENDUMS: 'SIGN_ADDENDUMS',
  SEND_REPORTS_VIA_HL7: 'SEND_REPORTS_VIA_HL7',
  VIEW_FAX_QUEUE: 'VIEW_FAX_QUEUE',
  // Discordance
  VIEW_DISCORDANCE: 'VIEW_DISCORDANCE',
  ACKNOWLEDGE_DISCORDANCE: 'ACKNOWLEDGE_DISCORDANCE',
  // Peer Review
  PERFORM_PEER_REVIEW: 'PERFORM_REER_REVIEW', // note: GWT has typo
  REQUEST_PEER_REVIEW: 'REQUEST_PEER_REVIEW',
  // Orders
  ADD_ORDERS: 'ADD_ORDERS',
  EDIT_ORDERS: 'EDIT_ORDERS',
  DELETE_ORDERS: 'DELETE_ORDERS',
  SEND_ORDERS_VIA_HL7: 'SEND_ORDERS_VIA_HL7',
  ARRIVE_PATIENT: 'ARRIVE_PATIENT',
  MATCH_WITH_ORDER: 'MATCH_WITH_ORDER',
  // Patients
  ADD_PATIENTS: 'ADD_PATIENTS',
  EDIT_PATIENTS: 'EDIT_PATIENTS',
  DELETE_PATIENTS: 'DELETE_PATIENTS',
  PATIENT_DISCOVERY: 'PATIENT_DISCOVERY',
  // Notes
  VIEW_STUDY_NOTES: 'VIEW_STUDY_NOTES',
  EDIT_STUDY_NOTES: 'EDIT_STUDY_NOTES',
  DELETE_STUDY_NOTES: 'DELETE_STUDY_NOTES',
  // QC
  QC_OPEN_EDITOR: 'QC_OPEN_EDITOR',
  QC_MARK_UNMARK: 'QC_MARK_UNMARK',
  QC_BREAK_SESSION: 'QC_BREAK_SESSION',
  // Users
  ADD_EDIT_ADMINISTRATORS: 'ADD_EDIT_ADMINISTRATORS',
  ADD_EDIT_REPORTING_PHYSICIANS: 'ADD_EDIT_REPORTING_PHYSICIANS',
  ADD_EDIT_RESIDENTS: 'ADD_EDIT_RESIDENTS',
} as const;
